import Theme from "context/theme";

// The token theme.css declares for the editor, in all three groups: `github_light_default` in the
// light `:root`, `tomorrow_night` in both dark groups. It holds an Ace MODULE NAME rather than a
// colour, which is the only reason this module exists — every other part of the theme lands through
// CSS and needs no JavaScript at all.
const VAR = "--bh-ace-theme";

// What to answer when the token cannot be read: no stylesheet (jest stubs every CSS import), or a
// paint before the sheet has applied. Light is the right answer in both — an editor that renders
// light on a light page is invisible as a defect, where the reverse is the dark island this phase
// exists to remove.
const FALLBACK = "github_light_default";

// One getComputedStyle per resolved theme for the life of the page, not one per render. An editor
// re-renders on every keystroke (its `value` prop is the parent's state), and reading a custom
// property forces a style recalculation — so the naive version turns typing into a recalc loop
// across every mounted editor.
//
// ONLY SUCCESSFUL READS ARE CACHED. Caching the fallback would freeze the wrong answer for the rest
// of the session if the first read ever landed before the stylesheet did.
const cache = {};

/**
 * The Ace theme module name for a resolved site theme.
 *
 * READ FROM CSS, NOT FROM A MAP HERE. theme.css is where every other palette decision is stated, and
 * a second table in JavaScript would be a second source of truth with nothing gating their
 * agreement — the dark-pairs gate in checkTokens.py already asserts that the two dark groups of
 * theme.css say the same thing, and that gate only watches the stylesheet.
 *
 * The two module names are still spelled once more, unavoidably, as the import paths in
 * `components/controls/editor.js`: an Ace theme has to be REGISTERED before it can be selected, and
 * a static import is what registration is. Those imports are load-bearing, not stylistic — see the
 * comment there.
 *
 * WHY THESE TWO, measured against this app's own planes rather than chosen by taste:
 *
 * `github_light_default` is the app's light palette already shipped as an Ace theme. Its background
 * #FFFFFF is the card white exactly — delta (0,0,0). Its foreground #24292E sits (3,4,5) off the body
 * ink #212529; the next-closest candidate, `dawn`, is (25,29,33) off and four themes ship pure black
 * at (33,37,41). Its active line #f6f8fa sits (2,2,2) off the sunken plane #f8fafc, and its comment
 * colour #6A737D is literally one of this app's own drift greys (--bh-text-muted-3).
 *
 * `tomorrow_night` is the only usable dark theme that sits BELOW the card. Under the epic's "lift"
 * decision the card #202024 is the top plane, so an inset editor must recess into it: #1D1F21 is
 * 7.94% below the card in relative luminance, landing between the sunken plane #131315 (-55%) and the
 * card. Every alternative floats above it — monokai +40.68%, github_dark +47.07%, dracula +61.73%,
 * one_dark +70.39%, tomorrow_night_eighties +78.82%, nord_dark +132.17% — which inverts the elevation
 * ladder. `gruvbox_dark_hard` is the one other theme below the card (-4.32%), and its default
 * foreground composites to 3.33:1.
 *
 * That is why monokai had to go, and it is a measurement rather than a preference: it was never a
 * dark theme chosen for this app, it was the react-ace example value copied to 22 call sites.
 */
export default class AceTheme {
  static name(resolved) {
    const key = resolved || Theme.resolved();
    if (cache[key]) {
      return cache[key];
    }
    // Guarded because it legitimately comes back empty under jest, exactly as the chrome-colour read
    // in context/theme.js is.
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(VAR).trim();
    if (!value) {
      return FALLBACK;
    }
    cache[key] = value;
    return value;
  }

  /** Test seam: drop the memo so a case can move the token under it. Nothing in the app calls it. */
  static __resetForTest() {
    for (const key of Object.keys(cache)) {
      delete cache[key];
    }
  }
}
